import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Formik, useFormikContext } from 'formik';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';

import useDialog from '../../../../web-app-components/hooks/useDialog';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';

import routesClient from '../../../../clients/routes';

import { fromForm, toForm } from '../../utils/formData';
import validationSchema from '../../utils/validationSchema';
import prepareFiles, { prepareMultipleFiles } from '../../utils/prepareFiles';

import Form from '../../components/Form';
import CoordinateAudioGuidesField from '../../components/Form/components/CoordinateAudioGuidesField';
import LanguageSelectField from '../../../Objects/components/Form/components/LanguageSelectField';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';

// Component to handle locale changes when user changes the dropdown
const LocaleChangeHandler = ({ currentLocale, onLocaleChange }) => {
  const { values } = useFormikContext();
  const prevFormLocaleRef = useRef(values.locale);
  const onLocaleChangeRef = useRef(onLocaleChange);
  onLocaleChangeRef.current = onLocaleChange;

  useEffect(() => {
    const formLocale = values.locale;
    const formLocaleChanged = formLocale !== prevFormLocaleRef.current;
    prevFormLocaleRef.current = formLocale;

    if (formLocaleChanged && formLocale !== currentLocale) {
      onLocaleChangeRef.current(formLocale);
    }
  }, [values.locale, currentLocale]);

  return null;
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(2),
  },
  sidebar: {
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
    },
  },
  languageSelect: {
    minWidth: 200,
  },
}));

const ExistingRoute = ({ id: idProp }) => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();

  const id = idProp?.trim();

  const { enqueueSnackbar } = useSnackbar();
  const confirmRemoveDialog = useDialog();

  const [data, setData] = useState(null);
  const [currentLocale, setCurrentLocale] = useState('lt-LT');
  const [loading, setLoading] = useState(true);
  const formikRef = useRef(null);
  const prevLocaleRef = useRef('lt-LT');

  const createMissingLocale = async (locale, sourceData) => {
    if (!sourceData) return null;
    const payload = fromForm({
      ...toForm(sourceData),
      locale,
    });
    // Do not copy POI object links when creating another locale - objects may not exist in that locale
    if (payload.CoordinateAudioGuides && payload.CoordinateAudioGuides.length > 0) {
      payload.CoordinateAudioGuides = payload.CoordinateAudioGuides.map((guide) => ({
        ...guide,
        object: null,
        subcategory: null,
      }));
    }
    return routesClient.createLocalization(id, payload, locale);
  };

  const loadDataForLocale = async (locale, updateForm = true, attemptedCreate = false) => {
    if (!id) return;
    try {
      const localeData = await routesClient.findOne(id, locale);
      setData(localeData);
      setCurrentLocale(locale);
      prevLocaleRef.current = locale;
      if (updateForm && formikRef.current) {
        formikRef.current.setValues(toForm({
          ...localeData,
          locale,
        }));
      }
    } catch (error) {
      // If locale doesn't exist, create it from default locale (lt-LT) then fetch again
      if (locale !== 'lt-LT') {
        try {
          const ltData = data || await routesClient.findOne(id, 'lt-LT');
          if (error?.response?.status === 404 && !attemptedCreate && ltData) {
            await createMissingLocale(locale, ltData);
            return loadDataForLocale(locale, updateForm, true);
          }

          setData(ltData);
          setCurrentLocale('lt-LT');
          prevLocaleRef.current = 'lt-LT';
          if (updateForm && formikRef.current) {
            formikRef.current.setValues(toForm({
              ...ltData,
              locale: 'lt-LT',
            }));
          }
        } catch (ltError) {
          console.warn('Failed to load or create locale', ltError);
          enqueueSnackbar(
            'Nepavyko gauti maršruto. Bandykite atidaryti iš naujo.',
            { variant: 'error' }
          );
          history.push('/routes');
        }
      } else {
        enqueueSnackbar('Maršrutas nerastas', { variant: 'error' });
        history.push('/routes');
      }
    }
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      enqueueSnackbar('Maršrutas nerastas', { variant: 'error' });
      history.push('/routes');
      return;
    }
    setLoading(true);
    (async () => {
      try {
        await loadDataForLocale('lt-LT', false);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, enqueueSnackbar, history]);

  const handleSubmit = async (values) => {
    if (!id) {
      enqueueSnackbar('Maršruto ID nerastas', { variant: 'error' });
      return;
    }

    try {
      const GXPFileId = values.GXPFile && !values.GXPFile.id 
        ? await prepareFiles(values.GXPFile) 
        : (values.GXPFile?.id || null);
      const VideoId = values.Video
        ? (values.Video.id ? values.Video.id : await prepareFiles(values.Video))
        : null;
      const ImagesIds = (values.Images && values.Images.length > 0)
        ? await prepareMultipleFiles(values.Images)
        : [];

      const preparedCoordinateGuides = await Promise.all(
        (values.CoordinateAudioGuides || []).map(async (guide) => {
          const audioFileId = guide.AudioFile && !guide.AudioFile.id
            ? await prepareFiles(guide.AudioFile)
            : (guide.AudioFile?.id || null);
          const objectId = guide.object != null && guide.object !== ''
            ? (typeof guide.object === 'object' && guide.object != null && 'id' in guide.object ? guide.object.id : guide.object)
            : null;
          const subcategoryId =
            guide.subcategory != null && guide.subcategory !== ''
              ? (typeof guide.subcategory === 'object' &&
                  guide.subcategory != null &&
                  'id' in guide.subcategory
                  ? guide.subcategory.id
                  : guide.subcategory)
              : null;
          return {
            ...guide,
            AudioFile: audioFileId,
            object: objectId,
            subcategory: subcategoryId,
          };
        })
      );
      
      const formData = fromForm({ 
        ...values, 
        GXPFile: GXPFileId,
        Video: VideoId,
        Images: ImagesIds,
        CoordinateAudioGuides: preparedCoordinateGuides,
      });
      
      const { locale, ...routeData } = formData;
      
      try {
        await routesClient.update(id, routeData, locale);
      } catch (updateError) {
        if (locale !== 'lt-LT') {
          try {
            await routesClient.createLocalization(id, routeData, locale);
          } catch (createError) {
            throw updateError;
          }
        } else {
          throw updateError;
        }
      }

      enqueueSnackbar('Maršrutas atnaujintas', { variant: 'success' });
      await loadDataForLocale(locale);
    } catch (error) {
      console.error('Error updating route:', error);
      enqueueSnackbar('Nepavyko atnaujinti maršruto. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  const handleRemoveClick = () => confirmRemoveDialog.setOpen(true);

  const handleRemoveConfirm = async () => {
    try {
      await routesClient.remove(id);
      enqueueSnackbar('Maršrutas pašalintas', { variant: 'success' });
      history.push('/routes');
    } catch (error) {
      console.error('Error deleting route:', error);
      enqueueSnackbar('Nepavyko pašalinti maršruto. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!data) {
    return null;
  }

  return (
    <div>
      <Formik
        key={`route-form-${id}-${currentLocale}`}
        innerRef={formikRef}
        initialValues={toForm({
          ...data,
          locale: currentLocale,
        })}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount={false}
      >
        {(formikProps) => (
          <>
            <LocaleChangeHandler
              currentLocale={currentLocale}
              onLocaleChange={(locale) => {
                if (data) {
                  loadDataForLocale(locale, true);
                }
              }}
            />
            <Box className={classes.header}>
              <Typography variant="h5">
                Redaguoti maršrutą
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
                <LanguageSelectField
                  name="locale"
                  label="Kalba / Language"
                  className={classes.languageSelect}
                />
                <ButtonSlim onClick={handleRemoveClick}>
                  Pašalinti maršrutą
                </ButtonSlim>
              </Box>
            </Box>
            <Grid container spacing={3} className={classes.container}>
            <Grid item xs={12} md={8}>
              <Form {...formikProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <div className={classes.sidebar}>
                <CoordinateAudioGuidesField
                  name="CoordinateAudioGuides"
                  gpxFile={formikProps.values.GXPFile}
                  disabled={formikProps.isSubmitting}
                  locale={formikProps.values.locale}
                />
              </div>
            </Grid>
          </Grid>
          <ConfirmRemoveDialog
            {...confirmRemoveDialog}
            onClose={() => confirmRemoveDialog.setOpen(false)}
            onConfirm={handleRemoveConfirm}
          />
          </>
        )}
      </Formik>
    </div>
  );
};

ExistingRoute.propTypes = {
  id: PropTypes.string,
};

export default ExistingRoute;
